/** Consultas e mutações. Uma chave por recurso; toda escrita invalida a projeção,
 * porque o saldo de todos os dias depende dela.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";
import { monthRange } from "./format";
import { useSession } from "./session";
import type { Category, Family, Scope, TransactionIn } from "./types";

export const keys = {
  projection: (params: object) => ["projection", params] as const,
  today: (scope: string) => ["today", scope] as const,
  accounts: ["accounts"] as const,
  /** Prefixo "accounts" de propósito: `invalidateMoney` já invalida a família inteira,
   *  então desarquivar uma conta atualiza a tela de Contas sem uma linha a mais. */
  accountsAll: ["accounts", "com-arquivadas"] as const,
  categories: ["categories"] as const,
  transactions: (params: object) => ["transactions", params] as const,
  recurrences: ["recurrences"] as const,
  plans: ["plans"] as const,
  statements: ["statements"] as const,
  statement: (id: string) => ["statement", id] as const,
  settings: ["settings"] as const,
  families: ["families"] as const,
};

/** Tudo que a escrita de dinheiro invalida. */
function invalidateMoney(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ["projection", "today", "transactions", "accounts", "statements", "plans", "recurrences", "scenarios"]) {
    qc.invalidateQueries({ queryKey: [k] });
  }
}

export function useProjection(params: {
  scope?: string;
  frm?: string;
  to?: string;
  scenario_id?: string | null;
}) {
  return useQuery({
    queryKey: keys.projection(params),
    queryFn: () => api.getProjection(params),
    staleTime: 30_000,
  });
}

/** O resumo que o motor monta para a tela Hoje: saldo realizado, colchão (`buffer`), menor
 * saldo do horizonte com data e causas, a confirmar, atrasados, diário que resta.
 *
 * A rota existe desde a F1 e nenhuma tela chamava (D1 da auditoria de 14/09). Atenção a uma
 * diferença que importa: `/today` não aceita cenário, então o que ele devolve é sempre o
 * mundo REAL — bom para o colchão e para o horizonte, que não mudam com a simulação. */
export const useToday = (scope: string) =>
  useQuery({ queryKey: keys.today(scope), queryFn: () => api.getToday(scope), staleTime: 30_000 });

/** A projeção do mês inteiro, que a tela Fluxo desenha em linhas. */
export function useMonthProjection(anyDayOfMonth: string, scope: string, scenarioId?: string | null) {
  const { first, last } = monthRange(anyDayOfMonth);
  return useProjection({ scope, frm: first, to: last, scenario_id: scenarioId ?? undefined });
}

/** Os lançamentos do mês, para a tela Fluxo ter os campos que a projeção não carrega
 * (recorrência, plano de parcelas, categoria, cenário) ao abrir um dia. */
export function useTransactionsOfMonth(anyDayOfMonth: string, scenarioId?: string | null) {
  const { first, last } = monthRange(anyDayOfMonth);
  const params = { frm: first, to: last, scenario_id: scenarioId ?? undefined, limit: 2000 };
  return useQuery({
    queryKey: keys.transactions(params),
    queryFn: () => api.getTransactions(params),
    staleTime: 30_000,
  });
}

export const useAccounts = () =>
  useQuery({ queryKey: keys.accounts, queryFn: () => api.getAccounts(), staleTime: 60_000 });

/** Inclui as arquivadas. Só a tela de arquivadas usa: em todo o resto do app, conta
 *  arquivada não existe — é para isso que ela foi arquivada. */
export const useAccountsComArquivadas = () =>
  useQuery({ queryKey: keys.accountsAll, queryFn: () => api.getAccounts(true), staleTime: 60_000 });

export const useCategories = () =>
  useQuery({ queryKey: keys.categories, queryFn: api.getCategories, staleTime: 300_000 });

export const useRecurrences = () =>
  useQuery({ queryKey: keys.recurrences, queryFn: api.getRecurrences, staleTime: 60_000 });

export const usePlans = () =>
  useQuery({ queryKey: keys.plans, queryFn: api.getPlans, staleTime: 60_000 });

export const useStatements = (status?: string) =>
  useQuery({ queryKey: [...keys.statements, status], queryFn: () => api.getStatements(status) });

export const useStatement = (id: string | null) =>
  useQuery({
    queryKey: keys.statement(id ?? ""),
    queryFn: () => api.getStatement(id as string),
    enabled: !!id,
  });

export const useSettingsQuery = () =>
  useQuery({ queryKey: keys.settings, queryFn: api.getSettings, staleTime: 300_000 });

/** As famílias de que o usuário é membro HOJE.
 *
 * `GET /families` devolve também a família que ele CRIOU e depois deixou: a política de
 * leitura precisa disso (`created_by_user_id = app_user_id()`) para o `INSERT ... RETURNING`
 * da criação funcionar antes de o criador virar membro. O efeito colateral aparecia como
 * "família zumbi" — 0 pessoas, nenhuma ação possível — na tela de Família, e como um seletor
 * Meu/Família de uma família que não é mais sua quando sobravam outros membros.
 *
 * Não existe `DELETE /families` na API, então apagar de verdade não é opção hoje. O filtro
 * fica aqui, num lugar só: duas telas leem esta lista e as duas erravam igual.
 */
export function useFamilies() {
  const { user } = useSession();
  return useQuery({
    queryKey: keys.families,
    queryFn: api.getFamilies,
    staleTime: 300_000,
    select: (fs: Family[]) => fs.filter((f) => (f.members ?? []).some((m) => m.user_id === user?.id)),
  });
}

// --- mutações ---------------------------------------------------------------

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransactionIn) => api.createTransaction(body),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
      scope = "single",
    }: {
      id: string;
      body: Partial<TransactionIn>;
      scope?: Scope;
    }) => api.updateTransaction(id, body, scope),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useDeleteTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scope = "single" }: { id: string; scope?: "single" | "from_here" }) =>
      api.deleteTransaction(id, scope),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useConfirmTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, amount, paid }: { id: string; amount: number; paid?: boolean }) =>
      api.confirmTransaction(id, amount, paid),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useClearTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paid_on }: { id: string; paid_on?: string }) =>
      api.clearTransaction(id, paid_on),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function usePayStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; amount?: number; paid_on?: string; from_account_id?: string }) =>
      api.payStatement(id, body),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createAccount,
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useCreateRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createRecurrence,
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createPlan,
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useUpdateRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof api.updateRecurrence>[1]) =>
      api.updateRecurrence(id, body),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useDeleteRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteRecurrence,
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deletePlan,
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof api.updateAccount>[1]) =>
      api.updateAccount(id, body),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: api.deleteAccount, onSuccess: () => invalidateMoney(qc) });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof api.updatePlan>[1]) =>
      api.updatePlan(id, body),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<Category>) => api.updateCategory(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
}

export function useCreateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createFamily,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.families }),
  });
}

export function useAddMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, email, role }: { familyId: string; email: string; role?: string }) =>
      api.addMember(familyId, email, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.families }),
  });
}

export function useShareAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, accountId, visibility }: { familyId: string; accountId: string; visibility?: string }) =>
      api.shareAccount(familyId, accountId, visibility),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useUnshareAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, accountId }: { familyId: string; accountId: string }) =>
      api.unshareAccount(familyId, accountId),
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.updateSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.settings });
      invalidateMoney(qc);
    },
  });
}
